import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../Badge';
import { cn } from '../../utils/cn';

export interface SidebarItem {
  key: string;
  label: string;
  icon: string;
  path?: string;
  href?: string;
  onClick?: () => void;
  badge?: string | number;
  children?: SidebarItem[];
}

export interface SidebarProps {
  items: SidebarItem[];
  activeItem?: string;
  onItemClick?: (item: SidebarItem) => void;
  isCollapsed?: boolean;
  isMobileOpen?: boolean;
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  items,
  activeItem,
  onItemClick,
  isCollapsed = false,
  isMobileOpen = false,
  className,
}) => {
  const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const toggleSubmenu = (key: string) => {
    const newOpenSubmenus = new Set(openSubmenus);
    if (newOpenSubmenus.has(key)) {
      newOpenSubmenus.delete(key);
    } else {
      newOpenSubmenus.add(key);
    }
    setOpenSubmenus(newOpenSubmenus);
  };

  const handleItemClick = (item: SidebarItem) => {
    if (item.children && item.children.length > 0) {
      toggleSubmenu(item.key);
    } else {
      const path = item.href || item.path;
      if (path) {
        navigate(path);
      }
      onItemClick?.(item);
    }
  };

  const renderItem = (item: SidebarItem, level: number = 0) => {
    const isActive = activeItem === item.key;
    const hasChildren = item.children && item.children.length > 0;
    const isSubmenuOpen = openSubmenus.has(item.key);
    const isSubmenuItem = level > 0;

    return (
      <div key={item.key}>
        <button
          onClick={() => handleItemClick(item)}
          className={cn(
            'flex items-center px-3 py-2 text-sm',
            'focus:outline-none',
            isSubmenuItem ? 'ml-4 w-[calc(100%-2rem)]' : 'w-full',
            isSubmenuItem ? 'rounded-md' : 'rounded-lg',
            isActive
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 shadow-sm'
              : 'text-gray-700 dark:text-gray-300 hover:bg-primary-50 dark:hover:bg-primary-900/20',
            isCollapsed && 'justify-center px-2',
            !isCollapsed && hasChildren && 'justify-between'
          )}
        >
          <div className="flex items-center space-x-3">
            <span className="material-icons text-lg">{item.icon}</span>
            {!isCollapsed && (
              <>
                <span className="font-semibold leading-27">{item.label}</span>
                {item.badge && (
                  <Badge variant="default" size="sm" className="ml-auto">
                    {item.badge}
                  </Badge>
                )}
              </>
            )}
          </div>
          {!isCollapsed && hasChildren && (
            <span
              className={cn('material-icons text-sm', isSubmenuOpen ? 'rotate-180' : 'rotate-0')}
            >
              expand_more
            </span>
          )}
        </button>

        {/* Submenu Items */}
        {hasChildren && !isCollapsed && isSubmenuOpen && (
          <div className="mt-1">
            <div className="space-y-1">
              {item.children?.map(child => renderItem(child, level + 1))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Simple approach: Different rendering for mobile vs desktop
  if (isMobileOpen) {
    // Mobile sidebar - fixed overlay
    return (
      <nav className="fixed top-0 left-0 h-full w-64 z-50 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out translate-x-0">
        <div className="p-4">
          <div className="space-y-1">{items.map(item => renderItem(item))}</div>
        </div>
      </nav>
    );
  }

  // Desktop sidebar - normal behavior
  return (
    <nav
      className={cn(
        'bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700',
        isCollapsed ? 'w-16' : 'w-64',
        'hidden md:block',
        className
      )}
    >
      <div className="p-4">
        <div className="space-y-1">{items.map(item => renderItem(item))}</div>
      </div>
    </nav>
  );
};
